<template>
    <div
        v-if="error"
        style="position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%)"
        class="has-text-centered"
    >
        {{ error }}
    </div>
</template>

<script>
import get from 'lodash/get.js';
import { useI18n } from 'vue-i18n';
import { createNamespacedHelpers } from 'vuex';

import { handleResponseError, initialState } from '@/Helpers.js';
import AutomatedSurveyAssignment from '@/models/AutomatedSurveyAssignment.js';
import MessageAlert from '@/models/MessageAlert.js';
import { trackAutomatedSurveyStarted } from '@/plugins/amplitude.js';
import axios from '@/plugins/axios.js';
import SnackbarService from '@/services/SnackbarService.js';
import EdverumToastService from '@/services/ToastService.js';
import store from '@/store/Index.js';

const { mapState } = createNamespacedHelpers('Auth');

/**
 * Find the next assignment in queue from store, LoadingState, or API
 */
async function findNextAssignmentInQueue(nextAssignmentId) {
    try {
        const nextAssignment = AutomatedSurveyAssignment.find(nextAssignmentId);
        if (nextAssignment) {
            return nextAssignment;
        }

        // Check LoadingState assignments
        const loadedAssignments = initialState('AutomatedSurveyAssignments', []);
        const loadedAssignment = loadedAssignments.find(a => a.id === nextAssignmentId);
        if (loadedAssignment) {
            await AutomatedSurveyAssignment.insert({ data: [loadedAssignment] });
            return AutomatedSurveyAssignment.find(nextAssignmentId);
        }

        // Fetch from API as last resort
        const { data: nextData } = await axios.get(`/api/automated-survey-attempts/next-in-queue`);
        if (nextData?.data?.id === nextAssignmentId) {
            await AutomatedSurveyAssignment.insert({ data: [nextData.data] });
            return AutomatedSurveyAssignment.find(nextAssignmentId);
        }
    } catch (error) {
        console.error('Failed to find next assignment in queue:', error);
    }
    return null;
}

export const routeToAutomatedSurveyAssignment = async (surveyAssignment, retryCount = 0) => {
    // Prevent infinite recursion
    if (retryCount > 1) {
        throw new Error('Unable to start survey. Please refresh the page and try again.');
    }

    try {
        const { data } = await axios.post(
            '/api/automated-survey-attempts',
            { automated_survey_assignment_id: surveyAssignment.id },
            { errorHandle: false }
        );

        // Track survey start for Amplitude Targeted Replay Capture
        await trackAutomatedSurveyStarted(surveyAssignment);

        return {
            name: 'automated-survey-attempt',
            params: {
                id: surveyAssignment.survey_id,
                assignmentId: surveyAssignment.id,
                attemptId: data.data.id,
            },
        };
    } catch (e) {
        const otherAttempt = get(e, 'response.data.meta.other_attempt');

        if (otherAttempt) {
            SnackbarService.open({
                message: 'You already have an active survey attempt. Redirecting...',
                type: 'is-danger',
                duration: 5000,
            });

            // Track survey start for Amplitude Targeted Replay Capture (resumed attempt)
            await trackAutomatedSurveyStarted(otherAttempt.automated_survey_assignment);

            return {
                name: 'automated-survey-attempt',
                params: {
                    id: otherAttempt.automated_survey_assignment.survey_id,
                    assignmentId: otherAttempt.automated_survey_assignment.id,
                    attemptId: otherAttempt.id,
                },
            };
        }

        // Handle queue order validation error (422)
        // Note: This only happens when a user manually clicks "Start Evaluation" on an assignment
        // that's not first in queue. We guide them to the correct one, but don't force auto-start.
        // Auto-start (forcing) only happens via AutomatedSurveyChecker.startLockedAutomatedSurveys()
        // which only acts on assignments where locked_at is in the past.
        if (e.response?.status === 422 && e.response?.data?.next_assignment_id) {
            const nextAssignmentId = e.response.data.next_assignment_id;
            const errorMessage =
                e.response.data.message || 'This survey is no longer the next in queue. Please refresh the page.';

            // Show error and guide user to correct assignment
            EdverumToastService.open({
                message: `${errorMessage} Starting the correct survey instead.`,
                type: 'is-warning',
                duration: 8000,
            });

            // Try to find and start the correct assignment
            // This is still a manual action (user clicked "Start Evaluation"), just correcting to the right one
            const nextAssignment = await findNextAssignmentInQueue(nextAssignmentId);
            if (nextAssignment) {
                // Recursively call with the correct assignment (with retry count)
                return routeToAutomatedSurveyAssignment(nextAssignment, retryCount + 1);
            }
        }

        throw e;
    }
};

// noinspection LocalVariableNamingConventionJS
export const AutomatedSurveyStarterMixin = {
    methods: {
        async startSurvey(surveyAssignment) {
            const loader = this.$loading.open({});

            try {
                const route = await routeToAutomatedSurveyAssignment(surveyAssignment);

                if (route.name) {
                    window.location.href = this.$router.resolve(route).href;
                }

                loader.close();
            } catch (e) {
                handleResponseError(e);

                loader.close();
            }
        },
    },
};

export default {
    name: 'AutomatedSurveyChecker',

    mixins: [AutomatedSurveyStarterMixin],

    emits: ['complete'],

    data: () => ({
        error: '',
        timeoutChecker: null,
    }),

    computed: {
        ...mapState(['user']),
    },

    setup() {
        const { t: translate } = useI18n();
        return { translate };
    },

    watch: {
        // Watch for route changes to re-check surveys when returning from completion
        $route(to, from) {
            // If coming back from an automated survey attempt, re-check for more locked surveys
            if (from?.name === 'automated-survey-attempt' && to?.name !== 'automated-survey-attempt') {
                this.performSurveyCheck();
            }
        },
    },

    created() {
        // Check initial state availability
        const initialAssignments = initialState('AutomatedSurveyAssignments', []);

        this.listenForLockedAutomatedSurveys();
        this.performSurveyCheck();
    },

    mounted() {
        // Double-check if we should run the check again after mounting
        if (this.$route.name !== 'automated-survey-attempt' && !this.$store.getters['User/hasActiveQuiz']) {
            // Optionally re-run the check if conditions haven't changed
        }
    },

    beforeUnmount() {
        this.error = '';

        if (this.user) {
            this.$echo.leave(`users-${this.user.id}`);
        }
    },

    methods: {
        performSurveyCheck() {
            if (this.$route.name === 'automated-survey-attempt') {
                this.$emit('complete');
                return;
            }

            // Check if user has an active quiz
            const userHasQuizActiveId = this.$store.getters['User/hasActiveQuiz'];

            // Proceed with navigation if user has an active quiz
            if (userHasQuizActiveId) {
                this.$emit('complete');
                return;
            }

            this.startLockedAutomatedSurveys();
        },

        listenForLockedAutomatedSurveys() {
            if (!this.user) {
                return;
            }

            this.$echo.private(`users-${this.user.id}`).notification(async notification => {
                if (notification.type !== 'App\\Notifications\\AutomatedSurveyTriggeredNotification') {
                    return;
                }

                await AutomatedSurveyAssignment.insert({ data: notification.assignment });

                const assignment = AutomatedSurveyAssignment.find(notification.assignment.id);

                if (!MessageAlert.find('id', notification.id)) {
                    await MessageAlert.insertFromNotification(notification);
                }

                if (assignment.is_locked_now) {
                    this.$store.commit('SET_AUTOMATED_SURVEY_ASSIGNMENT_READY', assignment);
                    this.$store.commit('SET_OUT_OF_DATE');
                }

                if (
                    this.user.quiz_active_id ||
                    ['quiz-attempt', 'automated-survey-attempt'].includes(this.$route.name)
                ) {
                    return;
                }

                let message = this.translate('you_have_a_new_automated_survey');

                if (
                    assignment.is_anonymous &&
                    initialState('InstitutionSettings.automated_surveys.anonymous_notification_banner', true)
                ) {
                    message += ` ${this.translate('this_automated_survey_is_anonymous')}`;
                }

                const params = {
                    type: 'is-primary',
                    position: 'is-bottom-left',
                    message,
                    duration: 5000,
                    queue: true,
                    indefinite: true,
                    actionText: 'Start Survey',
                    onAction: () => this.startSurvey(assignment),
                };

                if (!assignment.is_locked_now) {
                    params.cancelText = 'Later';
                }

                if (this.$route.name !== 'quiz-attempt') {
                    const snackbarNotification = this.$snackbar.open(params);

                    this.$store.commit('AutomatedSurveys/SET_NOTIFICATION', snackbarNotification);
                }
            });
        },

        async startLockedAutomatedSurveys() {
            this.timeoutChecker = setTimeout(() => {
                this.error =
                    'An error occurred while checking for surveys to complete. Please try refreshing the page or contacting support.';
            }, 1000 * 20);

            try {
                // Load assignments from initial state if any
                const loadedAssignments = initialState('AutomatedSurveyAssignments', []);

                if (loadedAssignments.length > 0) {
                    await AutomatedSurveyAssignment.insert({ data: loadedAssignments });
                }

                // Find locked assignments for the user that are not completed
                // Note: LoadingState already provides these in proper queue order (backend queue ordering)
                // is_locked_now only checks locked_at (for auto-start behavior)
                // Assignments with past required_at but future locked_at should NOT auto-start
                const lockedAssignments = AutomatedSurveyAssignment.query()
                    .where('user_id', this.user?.id)
                    .where(assignment => assignment.is_locked_now)
                    .where('completed_at', null)
                    .get();

                // Redirect if there are any locked assignments
                if (lockedAssignments.length > 0) {
                    // Take the first assignment - backend queue ordering ensures this is the correct next survey
                    const nextAssignment = lockedAssignments[0];

                    // Redirect to 'automated-survey-attempt' if conditions are met
                    if (nextAssignment.survey_attempt_id && this.$route.name !== 'automated-survey-attempt') {
                        this.complete();

                        return this.$router.push({
                            name: 'automated-survey-attempt',
                            params: {
                                id: nextAssignment.survey_id,
                                assignmentId: nextAssignment.id,
                                attemptId: nextAssignment.survey_attempt_id,
                            },
                        });
                    }

                    // Calculate route for the next assignment in queue with retry logic
                    try {
                        const assignmentRoute = await this.createSurveyAttemptWithRetry(nextAssignment);

                        // Open a toast message based on the assignment type
                        const message = nextAssignment.course_id ? 'Starting evaluation...' : 'Starting survey...';
                        SnackbarService.open({ message });

                        // Dismiss any notifications related to automated surveys and set redirect info
                        await store.dispatch('AutomatedSurveys/dismissNotification');
                        store.commit('AutomatedSurveys/SET_REDIRECT', this.$router.resolve({ name: 'home' }));

                        // Redirect to the calculated route
                        window.location.href = this.$router.resolve(assignmentRoute).href;

                        return true;
                    } catch (e) {
                        console.error('[DEBUG-AutomatedSurveyChecker] Failed to start survey after retries:', e);
                        this.error = `Unable to start survey: ${e.message}. Please refresh the page or contact support.`;
                        handleResponseError(e);
                    }
                }
            } catch (e) {
                this.error = e.message;
            } finally {
                this.complete();
            }

            return true;
        },

        /**
         * Create survey attempt with retry logic for network failures
         */
        async createSurveyAttemptWithRetry(assignment, maxRetries = 3) {
            let lastError;

            // Helper function to create a delay
            const delay = ms =>
                new Promise(resolve => {
                    setTimeout(resolve, ms);
                });

            // Recursive retry function to avoid await in loop
            const attemptRequest = async attempt => {
                try {
                    // Use the existing function with error handling
                    const route = await routeToAutomatedSurveyAssignment(assignment);
                    return route;
                } catch (error) {
                    lastError = error;
                    console.warn(
                        `[DEBUG-AutomatedSurveyChecker] Survey creation attempt ${attempt} failed:`,
                        error.message
                    );

                    // Don't retry on validation errors (4xx), only network/server errors (5xx)
                    if (error.response?.status >= 400 && error.response?.status < 500) {
                        console.error('[DEBUG-AutomatedSurveyChecker] Client error detected, not retrying');
                        throw error;
                    }

                    // If we haven't exceeded max retries, wait and try again
                    if (attempt < maxRetries) {
                        const delayMs = 2 ** (attempt - 1) * 1000; // 1s, 2s, 4s
                        console.warn(`[DEBUG-AutomatedSurveyChecker] Waiting ${delayMs}ms before retry...`);
                        await delay(delayMs);
                        return attemptRequest(attempt + 1);
                    }

                    // All attempts failed
                    console.error(
                        `[DEBUG-AutomatedSurveyChecker] All ${maxRetries} attempts failed for assignment ${assignment.id}`
                    );
                    throw error;
                }
            };

            return attemptRequest(1);
        },

        complete() {
            this.$emit('complete');

            clearTimeout(this.timeoutChecker);
        },

        async checkForPrefilledAnswers(assignment) {
            try {
                const { data } = await axios.post(
                    '/api/automated-survey-attempts',
                    { automated_survey_assignment_id: assignment.id },
                    { errorHandle: false }
                );

                // Check if there are pre-filled answers
                if (data.data.prefilled_questions_count > 0) {
                    const totalQuestions = data.data.total_questions_count;
                    const prefilledCount = data.data.prefilled_questions_count;

                    await this.showPrefilledModal(assignment, prefilledCount, totalQuestions);
                }

                return data;
            } catch (e) {
                // Handle existing attempt redirect logic
                const otherAttempt = get(e, 'response.data.meta.other_attempt');
                if (otherAttempt) {
                    SnackbarService.open({
                        message: 'You already have an active survey attempt. Redirecting...',
                        type: 'is-danger',
                        duration: 5000,
                    });
                }
                throw e;
            }
        },

        async showPrefilledModal(assignment, prefilledCount, totalQuestions) {
            const isEvaluation = !!assignment.course_id;
            const itemType = isEvaluation ? 'evaluation' : 'survey';
            const remainingCount = totalQuestions - prefilledCount;

            let message = `This ${itemType} contains questions you've answered before. `;
            message += `${prefilledCount} out of ${totalQuestions} questions have been pre-filled with your previous answers to save you time.`;

            if (remainingCount > 0) {
                message += ` You have ${remainingCount} new question${remainingCount === 1 ? '' : 's'} to complete.`;
            }

            return new Promise(resolve => {
                this.$modal.open({
                    parent: this,
                    component: {
                        template: `
                            <div class="modal-card" style="width: auto">
                                <header class="modal-card-head">
                                    <p class="modal-card-title">
                                        <e-icon icon="info-circle" type="is-info"></e-icon>
                                        Pre-filled ${isEvaluation ? 'Evaluation' : 'Survey'}
                                    </p>
                                </header>
                                <section class="modal-card-body">
                                    <div class="content">
                                        <p>${message}</p>
                                        <p class="has-text-subtle is-size-7">
                                            You can review and modify any pre-filled answers during the ${itemType}.
                                        </p>
                                    </div>
                                </section>
                                <footer class="modal-card-foot">
                                    <e-button @click="$emit('close')" type="is-primary">
                                        Continue
                                    </e-button>
                                    <e-button @click="$emit('close')" type="is-light">
                                        View Details Later
                                    </e-button>
                                </footer>
                            </div>
                        `,
                        props: ['isEvaluation'],
                        data: () => ({ isEvaluation, message }),
                    },
                    hasModalCard: true,
                    onCancel: resolve,
                    customClass: 'prefilled-survey-modal',
                });
            });
        },
    },
};
</script>
